import { DateInputLocalized } from '@components'

import { Button, Flex, LottieAnimation, notify, Space, Spacing, Text } from '@billy/design-system'
import subYears from 'date-fns/subYears'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDirtyRoute } from '../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useScoutConnectionId } from '../../hooks/useScoutConnectionId'
import { useUpdateScoutConnection } from '../../hooks/useUpdateScoutConnection'

export const ScoutStartDatePicker = (): ReactElement => {
  const [startDate, setStartDate] = useState<Date | undefined | null>(new Date())
  const { t } = useTranslation()
  const { setDirty } = useDirtyRoute()
  const { scoutConnectionId, setScoutConnectionId } = useScoutConnectionId()

  const { update: updateScoutConnection, isProcessing: isUpdating } = useUpdateScoutConnection({
    onSuccess: () => {
      setDirty(false)
      setScoutConnectionId(null)
      notify({
        id: NotificationKeys.ScoutConnectionUpdate,
        message: t('scout.connected.date_picker.success'),
        variant: 'success',
      })
    },
    onError: () => {
      notify({
        id: NotificationKeys.ScoutConnectionUpdate,
        message: t('scout.connected.date_picker.error'),
        variant: 'error',
      })
      setScoutConnectionId(null)
    },
  })

  const minDate = subYears(new Date(), 1) // 1 year ago

  const handleSubmitButtonClick = useCallback(() => {
    startDate &&
      scoutConnectionId &&
      updateScoutConnection({
        connectionId: scoutConnectionId,
        lastFetchedAt: startDate.toISOString(),
      })
  }, [startDate, updateScoutConnection, scoutConnectionId])

  useEffect(() => {
    setDirty(true)
  }, [setDirty])

  return (
    <Flex alignItems="center" justifyContent="center" flexDirection="column">
      <LottieAnimation height={100} width={100} isClickToPauseDisabled name="invoicesEmptyState" />
      <Space size="s24" />
      <Text variant="h2">{t('scout.connected.date_picker.title')}</Text>
      <Space size="s16" />
      <Text alignment="center" colorVariant="secondary" variant="bodyLG">
        {t('scout.connected.date_picker.description')}
      </Text>
      <Space size="s24" />
      <Flex alignItems="center" justifyContent="center" flexDirection="column" gap={Spacing.S16}>
        <DateInputLocalized
          bordered
          defaultValue={startDate}
          minDate={minDate}
          value={startDate}
          onChange={setStartDate}
          size="md"
        />
        <Button disabled={!startDate} loading={isUpdating} onClick={handleSubmitButtonClick}>
          {t('confirm')}
        </Button>
      </Flex>
    </Flex>
  )
}
